import {
  Accordion,
  ActionIcon,
  AppShell,
  Badge,
  Button,
  colorsTuple,
  createTheme,
  CSSVariablesResolver,
  DefaultMantineColor,
  defaultVariantColorsResolver,
  Input,
  InputWrapper,
  MantineColorsTuple,
  Menu,
  Modal,
  NavLink,
  Paper,
  Radio,
  rem,
  RingProgress,
  Select,
  Table,
  Tabs,
  useMantineTheme,
  VariantColorsResolver,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import accordionClasses from './Accordion.module.css';
import badgeClasses from './Badge.module.css';
import buttonClasses from './Button.module.css';
import modalRevampClasses from './ModalRevamp.module.css';
import navLinkClasses from './NavLink.module.css';
import paperClasses from './Paper.module.css';
import radioClasses from './Radio.module.css';
import dateInputClasses from './DateInput.module.css';
import ringProgressClasses from './RingProgrss.module.css';
import themeClasses from './Theme.module.css';
import { DateInput } from '@mantine/dates';

export function resolveMantineColor(color: string = 'white') {
  const theme = useMantineTheme();
  const [base, shade] = color.split('.');
  if (base in theme.colors) {
    const shadeIndex = shade ? parseInt(shade, 10) : 6;
    return theme.colors[base][shadeIndex];
  }
  return color;
}

const colors: Partial<Record<DefaultMantineColor, MantineColorsTuple>> = {
  gray: [
    '#F9F9F9', // 0
    '#F2F2F2', // 1
    '#E5E5E5', // 2
    '#CCCCCC', // 3
    '#B2B2B2', // 4
    '#999999', // 5
    '#808080', // 6
    '#666666', // 7
    '#4D4D4D', // 8
    '#333333', // 9
    '#1A1A1A', // 10
  ],
  brand: [
    '#FFF4EE', // 0
    '#FFE4D2', // 1
    '#FFCFB0', // 2
    '#FFB482', // 3
    '#FFA060', // 4
    '#FF8E42', // 5
    '#FF771C', // 6
    '#D96518', // 7
    '#C85D15', // 8
    '#994711', // 9
    '#66300B', // 10
  ],
  error: [
    '#FFFBFA', // 0
    '#FEF3F2', // 1
    '#FEE4E2', // 2
    '#FECDCA', // 3
    '#FDA29B', // 4
    '#F97066', // 5
    '#F04438', // 6
    '#D92D20', // 7
    '#BE2318', // 8
    '#912018', // 9
    '#7A271A', // 10
  ],
  warning: [
    '#FFFCF5', // 0
    '#FFFAEB', // 1
    '#FEF0C7', // 2
    '#FEDF89', // 3
    '#FEC84B', // 4
    '#FDB022', // 5
    '#F79009', // 6
    '#DC6803', // 7
    '#B54708', // 8
    '#93370D', // 9
    '#7A2E0E', // 10
  ],
  success: [
    '#F6FEF9', // 0
    '#ECFDF3', // 1
    '#D1FADF', // 2
    '#A6F4C5', // 3
    '#6CE9A6', // 4
    '#32D583', // 5
    '#12B76A', // 6
    '#039855', // 7
    '#027A48', // 8
    '#05603A', // 9
    '#054F31', // 10
  ],
  blueGray: [
    '#F8F9FC', // 0
    '#F1F5F9', // 1
    '#E2E8F0', // 2
    '#CBD5E1', // 3
    '#94A3B8', // 4
    '#64748B', // 5
    '#475569', // 6
    '#334155', // 7
    '#1E293B', // 8
    '#0F172A', // 9
    '#020617', // 10
  ],
  blueLight: [
    '#F0F9FF', // 0
    '#E0F2FE', // 1
    '#BAE6FD', // 2
    '#7DD3FC', // 3
    '#38BDF8', // 4
    '#0EA5E9', // 5
    '#0284C7', // 6
    '#0369A1', // 7
    '#075985', // 8
    '#0C4A6E', // 9
    '#082F49', // 10
  ],
  blue: [
    '#EFF6FF', // 0
    '#DBEAFE', // 1
    '#BFDBFE', // 2
    '#93C5FD', // 3
    '#60A5FA', // 4
    '#3B82F6', // 5
    '#2563EB', // 6
    '#1D4ED8', // 7
    '#1E40AF', // 8
    '#1E3A8A', // 9
    '#172554', // 10
  ],
  indigo: [
    '#EEF2FF', // 0
    '#E0E7FF', // 1
    '#C7D2FE', // 2
    '#A5B4FC', // 3
    '#818CF8', // 4
    '#6366F1', // 5
    '#4F46E5', // 6
    '#4338CA', // 7
    '#3730A3', // 8
    '#312E81', // 9
    '#1E1B4B', // 10
  ],
  purple: [
    '#FAF5FF', // 0
    '#F3E8FF', // 1
    '#E9D5FF', // 2
    '#D8B4FE', // 3
    '#C084FC', // 4
    '#A855F7', // 5
    '#9333EA', // 6
    '#7E22CE', // 7
    '#6B21A8', // 8
    '#581C87', // 9
    '#3B0764', // 10
  ],
  pink: [
    '#FDF2F8', // 0
    '#FCE7F3', // 1
    '#FBCFE8', // 2
    '#F9A8D4', // 3
    '#F472B6', // 4
    '#EC4899', // 5
    '#DB2777', // 6
    '#BE185D', // 7
    '#9D174D', // 8
    '#831843', // 9
    '#500724', // 10
  ],
  rose: [
    '#FFF1F2', // 0
    '#FFE4E6', // 1
    '#FECDD3', // 2
    '#FDA4AF', // 3
    '#FB7185', // 4
    '#F43F5E', // 5
    '#E11D48', // 6
    '#BE123C', // 7
    '#9F1239', // 8
    '#881337', // 9
    '#4C0519', // 10
  ],
  orange: [
    '#FFFAF5', // 0
    '#FFF6ED', // 1
    '#FFEAD5', // 2
    '#FDDCAB', // 3
    '#FEB273', // 4
    '#FD853A', // 5
    '#FB6514', // 6
    '#EC4A0A', // 7
    '#C4320A', // 8
    '#9C2A10', // 9
    '#7E2410', // 10
  ],
  contentBackground: colorsTuple('#F6F6F4'),
  navBackground: colorsTuple('#121211'),
};

const variantColorResolver: VariantColorsResolver = input => {
  const defaultResolvedColors = defaultVariantColorsResolver(input);

  // Completely override variant
  if (input.variant === 'success') {
    return {
      background: 'var(--mantine-color-success-0)',
      border: '1px solid var(--mantine-color-success-3)',
      color: 'var(--mantine-color-success-8)',
      hover: 'none',
    };
  }

  // Add new variants support
  if (input.variant === 'error') {
    return {
      background: 'var(--mantine-color-red-5)',
      color: 'var(--mantine-color-red-10)',
      hover: 'none',
      border: 'none',
    };
  }

  if (input.variant === 'processing') {
    return {
      background: 'var(--mantine-color-gray-2)',
      color: 'var(--mantine-color-gray-8)',
      hover: 'none',
      border: 'none',
    };
  }

  if (input.variant === 'brand') {
    return {
      background: 'var(--mantine-color-brand-1)',
      border: '1px solid var(--mantine-color-brand-3)',
      color: 'var(--mantine-color-brand-8)',
      hover: 'none',
    };
  }

  if (input.variant === 'pink') {
    return {
      background: 'var(--mantine-color-pink-1)',
      border: '1px solid var(--mantine-color-pink-3)',
      color: 'var(--mantine-color-pink-8)',
      hover: 'none',
    };
  }

  if (input.variant === 'gray') {
    return {
      background: 'var(--mantine-color-gray-1)',
      border: '1px solid var(--mantine-color-gray-3)',
      color: 'var(--mantine-color-gray-8)',
      hover: 'none',
    };
  }

  return defaultResolvedColors;
};

// Backwards compatibility aliases
colors.lightBlue = colors.blueLight;
colors.green = colors.success;

export const FONT_WEIGHTS = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export function createMantineTheme() {
  return createTheme({
    focusRing: 'auto',
    fontFamily: 'Manrope',
    fontSizes: {
      xs: rem(12),
      sm: rem(14),
      md: rem(16),
      lg: rem(18),
      xl: rem(20),
      xxl: rem(24),
      xxxl: rem(48),

      textXs: rem(12),
      textSm: rem(14),
      textMd: rem(16),
      textLg: rem(18),
      textXl: rem(20),
      displayXs: rem(24),
      displaySm: rem(30),
      displayMd: rem(36),
      displayLg: rem(48),
      displayXl: rem(60),
      display2xl: rem(72),
    },
    headings: {
      fontWeight: '400',

      sizes: {
        h1: { fontSize: rem(72) },
        h2: { fontSize: rem(60) },
        h3: { fontSize: rem(48) },
        h4: { fontSize: rem(36) },
        h5: { fontSize: rem(30) },
        h6: { fontSize: rem(24) },
      },
    },
    primaryColor: 'brand',
    colors,
    spacing: {
      xxxs: rem(4),
      xxs: rem(8),
      xs: rem(12),
      sm: rem(16),
      md: rem(20),
      lg: rem(24),
      xl: rem(28),
      xxl: rem(32),
      xxxl: rem(40),
      xxxxl: rem(64),
    },
    breakpoints: {
      xs: '30em',
      sm: '48em',
      md: '60em',
      lg: '70em',
      xl: '80em',
      xxl: '100em',
    },
    shadows: {
      'xs-with-inner':
        '0px 1.25px 2.5px rgba(10, 13, 18, 0.05), inset 0px -1.5px 0px rgba(10, 13, 18, 0.05)',
      xl: '0px 3px 3px -1.5px rgba(10, 13, 18, 0.04), 0px 8px 8px -4px rgba(10, 13, 18, 0.03)',
    },
    components: {
      Loader: {
        classNames: {
          root: themeClasses.loader,
        },
        defaultProps: {
          'data-testid': 'mantine-loader',
        },
      },
      Modal: Modal.extend({
        classNames: modalRevampClasses,
      }),
      Button: Button.extend({
        classNames: buttonClasses,
      }),
      ActionIcon: ActionIcon.extend({
        classNames: {
          root: themeClasses.actionIconRoot,
        },
      }),
      Menu: Menu.extend({
        classNames: {
          dropdown: themeClasses.menuDropdown,
        },
      }),
      Select: Select.extend({
        classNames: {
          label: themeClasses.inputLabel,
          option: themeClasses.selectOption,
        },
        defaultProps: {
          rightSection: <IconChevronDown stroke={1} size={18} />,
        },
      }),
      InputWrapper: InputWrapper.extend({
        classNames: {
          label: themeClasses.inputLabel,
        },
      }),
      Divider: {
        classNames: {
          root: themeClasses.divider,
        },
      },
      Input: Input.extend({
        classNames: {
          wrapper: themeClasses.inputWrapper,
          input: themeClasses.input,
        },
      }),
      AppShell: AppShell.extend({
        styles: {
          aside: {
            boxShadow:
              '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
          },
        },
      }),
      Tabs: Tabs.extend({
        classNames: {
          tab: themeClasses.tab,
        },
      }),
      Table: Table.extend({
        classNames: {
          thead: themeClasses.thead,
          th: themeClasses.th,
        },
      }),
      NavLink: NavLink.extend({ classNames: navLinkClasses }),
      Paper: Paper.extend({
        classNames: paperClasses,
      }),
      Accordion: Accordion.extend({
        classNames: accordionClasses,
      }),
      RingProgress: RingProgress.extend({
        classNames: ringProgressClasses,
      }),
      Badge: Badge.extend({
        classNames: badgeClasses,
      }),
      Radio: Radio.extend({
        classNames: radioClasses,
      }),
      DateInput: DateInput.extend({
        classNames: dateInputClasses,
      }),
    },
    variantColorResolver,
  });
}

export const cssVariableResolver: CSSVariablesResolver = theme => ({
  variables: {
    '--mantine-spacing-xxxs': theme.spacing.xxxs,
    '--mantine-spacing-xxs': theme.spacing.xxs,
    '--mantine-spacing-xxl': theme.spacing.xxl,
    '--mantine-spacing-xxxl': theme.spacing.xxxl,
    '--default-border-color': 'var(--mantine-color-gray-2)',
    '--solid-border': '1px solid var(--default-border-color)',
  },
  light: {},
  dark: {},
});
